"use client";

import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { MotionPrismicNextLink } from "@/components/ui/motion-prismic-next-link";
import clsx from "clsx";
import employabilityAndCareerServiceSectionLeftShape from "@/assets/employabilityAndCareerServiceSectionLeftShape.svg";
import employabilityAndCareerServiceSectionRightShape from "@/assets/employabilityAndCareerServiceSectionRightShape.svg";
import Image from "next/image";
import noise from "@/assets/noise.png";

/**
 * Props for `EmployabilityAndCareersServicesSection`.
 */
export type EmployabilityAndCareersServicesSectionProps =
  SliceComponentProps<Content.EmployabilityAndCareersServicesSectionSlice>;

/**
 * Component for "EmployabilityAndCareersServicesSection" Slices.
 */
const EmployabilityAndCareersServicesSection: FC<
  EmployabilityAndCareersServicesSectionProps
> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white py-10 px-4 md:px-[30px] lg:px-[60px] xl:px-20"
    >
      <Bounded className="relative bg-[#033D61] rounded-3xl p-8 container mx-auto overflow-hidden">
        <Image
          src={employabilityAndCareerServiceSectionLeftShape.src}
          alt="left shape"
          width={521}
          height={482}
          className="absolute left-[-50%] sm:left-[-20%] md:-left-5 top-0 md:top-[-40%] h-[600px] w-auto pointer-events-none select-none"
        />
        <Image
          src={employabilityAndCareerServiceSectionRightShape.src}
          alt="right shape"
          width={521}
          height={482}
          className="absolute right-[-50%] sm:right-[-20%] md:-right-5 bottom-0 md:top-[-40%] h-[600px] w-auto pointer-events-none select-none"
        />
        <div
          className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.4]"
          style={{
            background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <PrismicRichText field={slice.primary.title} components={{
            heading1: ({ children }) => (
              <h1 className="text-[#FAFDFF] font-archivo text-3xl font-semibold leading-10 mb-[27px] relative max-w-[1166px]">{children}</h1>
            ),
            heading2: ({ children }) => (
              <h2 className="text-[#FAFDFF] font-archivo text-3xl font-semibold leading-10 mb-[27px] relative max-w-[1166px]">{children}</h2>
            ),
            heading3: ({ children }) => (
              <h3 className="text-[#FAFDFF] font-archivo text-3xl font-semibold leading-10 mb-[27px] relative max-w-[1166px]">{children}</h3>
            ),
            heading4: ({ children }) => (
              <h4 className="text-[#FAFDFF] font-archivo text-3xl font-semibold leading-10 mb-[27px] relative max-w-[1166px]">{children}</h4>
            ),
            heading5: ({ children }) => (
              <h5 className="text-[#FAFDFF] font-archivo text-3xl font-semibold leading-10 mb-[27px] relative max-w-[1166px]">{children}</h5>
            ),
            heading6: ({ children }) => (
              <h6 className="text-[#FAFDFF] font-archivo text-3xl font-semibold leading-10 mb-[27px] relative max-w-[1166px]">{children}</h6>
            ),
          }} />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <PrismicRichText field={slice.primary.description} components={{
            paragraph: ({ children }) => (
              <p className="text-[#FAFDFF] font-merriweather text-lg font-normal leading-7 mb-12 relative max-w-[1166px]">{children}</p>
            )
          }} />
        </motion.div>
        <div className="flex flex-col md:flex-row items-center gap-[9px] relative">
          <MotionPrismicNextLink
            field={slice.primary.first_button_link}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <Button
              variant={
                slice.primary.first_button_link.variant === "Primary"
                  ? "default"
                  : "outline"
              }
              className={clsx(
                "px-5 py-3 cursor-pointer font-archivo text-base font-medium leading-6",
                slice.primary.first_button_link.variant === "Primary"
                  ? "rounded-3xl bg-[linear-gradient(264deg,#0798F2_-29%,#43D9BD_85.45%)] hover:bg-[linear-gradient(264deg,#43D9BD_-29%,#0798F2_85.45%)] text-[#033D61]"
                  : "rounded-[40px] border-[#EBF7FF] text-[#FAFDFF]"
              )}
            >
              {slice.primary.first_button_link.text}
            </Button>
          </MotionPrismicNextLink>
          <MotionPrismicNextLink
            field={slice.primary.first_button_link}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <Button
              variant={
                slice.primary.second_button_link.variant === "Primary"
                  ? "default"
                  : "outline"
              }
              className={clsx(
                "px-5 py-3 cursor-pointer font-archivo text-base font-medium leading-6",
                slice.primary.second_button_link.variant === "Primary"
                  ? "rounded-3xl bg-[linear-gradient(264deg,#0798F2_-29%,#43D9BD_85.45%)] hover:bg-[linear-gradient(264deg,#43D9BD_-29%,#0798F2_85.45%)] text-[#033D61]"
                  : "rounded-[40px] bg-transparent border-[#EBF7FF] text-[#FAFDFF]"
              )}
            >
              {slice.primary.second_button_link.text}
            </Button>
          </MotionPrismicNextLink>
        </div>
      </Bounded>
    </section>
  );
};

export default EmployabilityAndCareersServicesSection;
